import { ProgressStatus } from 'Components/ProgressBar';
import { IDocument } from '../documents';
import api, {
  buildQueryParams,
  IGetQueryParams,
  IPaginatedRepsonse,
} from '../index';
import { IUser } from '../user';

export interface ITicket {
  id: number;
  title: string;
  description: string;
  category: string;
  status: TicketStatusEnum;
  created_at: string;
  documents: IDocument[];
  updated_at: string;
  user: IUser;
}
export enum TicketStatusEnum {
  Todo = ProgressStatus.Todo,
  InProgress = ProgressStatus.InProgress,
  Done = ProgressStatus.Done,
}
export const TicketStatuses = [
  { id: TicketStatusEnum.Todo, label: ProgressStatus.Todo },
  {
    id: TicketStatusEnum.InProgress,
    label: ProgressStatus.InProgress,
  },
  { id: TicketStatusEnum.Done, label: 'Resolved' },
];

export const TicketCategories = [
  {
    name: 'General Inquiry',
    id: 'General Inquiry',
  },
  {
    name: 'Request a Demo',
    id: 'Request a Demo',
  },
  {
    name: 'Pricing/Quote Request',
    id: 'Pricing/Quote Request',
  },
  {
    name: 'Contact Technical Support',
    id: 'Contact Technical Support',
  },
  {
    name: 'Billing Question',
    id: 'Billing Question',
  },
];

export const ticketsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getTicketsCategories: builder.query<string[], void>({
      query: () => '/categories',
      transformResponse: (response: { job_types: string[] }) =>
        response.job_types,
    }),

    getTickets: builder.query<
      IPaginatedRepsonse<ITicket>,
      IGetQueryParams<{
        search_keyword?: string;
        status?: TicketStatusEnum[];
      }>
    >({
      query: (params) => '/reported_problems' + buildQueryParams(params),
      providesTags: [{ type: 'Tickets', id: 'List' }],
    }),

    getTicketById: builder.query<ITicket, number>({
      query: (id) => `/reported_problems/${id}`,
      providesTags: (ticket) => [{ type: 'Ticket', id: ticket?.id }],
      transformResponse: (response: { reported_problem: ITicket }) =>
        response.reported_problem,
    }),

    submitTicket: builder.mutation<
      { reported_problem: ITicket },
      {
        title: string;
        description: string;
        category: string;
        document_ids?: number[];
      }
    >({
      query: (body) => {
        const ticket = {
          reported_problem: {
            title: body.title,
            description: body.description,
            category: body.category,
            document_ids: body.document_ids,
          },
        };

        return {
          url: `/reported_problems`,
          method: 'POST',
          body: ticket,
        };
      },
      invalidatesTags: [{ type: 'Tickets', id: 'List' }],
    }),

    updateTicket: builder.mutation<ITicket, Partial<ITicket>>({
      query: (reported_problem: Partial<ITicket>) => {
        const body = {
          reported_problem,
        };
        return {
          url: `/reported_problems/${body.reported_problem.id}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Tickets', id: 'List' },
        { type: 'Ticket', id: arg.id },
      ],
      transformResponse: (response: { task: ITicket }) => response.task,
    }),
  }),
});

export const {
  useGetTicketsCategoriesQuery,
  useUpdateTicketMutation,
  useGetTicketsQuery,
  useLazyGetTicketsQuery,
  useSubmitTicketMutation,
  useGetTicketByIdQuery,
} = ticketsApi;
